/**
 * 存储产品线
 * 结构：软件（芯存 XNAS 软件）+ 一体机（机架式存储产品下四款型号）
 * 硬件文案对齐用户提供的图 3；软件详情页仅展示《存储-软件产品介绍》PPT 全文（见 nasStorageSoftwarePpt.json）
 */

import xc3000WhitepaperRaw from '../../tmp_storage_extract/企业级XC-3000系列全闪NAS存储技术白皮书.txt?raw';
import xc2000WhitepaperRaw from '../../tmp_storage_extract/企业级XC-2000系列NAS存储技术白皮书.txt?raw';
import xc1000WhitepaperRaw from '../../tmp_storage_extract/机架式XC-1000系列NAS存储技术白皮书.txt?raw';
import xc08WhitepaperRaw from '../../tmp_storage_extract/桌面XC-08信创系列NAS存储技术白皮书.txt?raw';

export const storageFamilyIntro =
  '瑞和云图存储产品线以「芯存 XNAS 软件」为新一代智能存储操作系统底座，以「机架式存储产品」提供一站式、高适配性的数据存储能力，覆盖从桌面信创到企业级全闪与混闪场景。';

export const storageFeatured = {
  title: '芯存 XNAS 与机架式存储',
  subtitle: '软件定义存储操作系统 + 机架式一体化交付',
  description:
    '芯存 XNAS 软件面向通用国产服务器与信创生态；机架式存储产品整合 XC-3000 / XC-2000 / XC-1000 与 XC-08 等系列，满足高性能、规模化扩展与敏捷部署等多样化需求。',
};

/**
 * @typedef {Object} StorageRowItem
 * @property {string} slug
 * @property {string} name
 * @property {string} path
 * @property {string} description
 */

/** @type {{ label: string, tagline?: string, groupTitle?: string, groupTagline?: string, items: StorageRowItem[] }[]} */
export const storageRows = [
  {
    label: '软件',
    tagline: '',
    items: [
      {
        slug: 'software',
        name: '芯存 XNAS 软件',
        path: '/product/storage/software',
        description: '新一代智能存储操作系统',
      },
    ],
  },
  {
    label: '一体机',
    groupTitle: '机架式存储产品',
    groupTagline: '提供一站式、高适配性的数据存储',
    tagline: '',
    items: [
      {
        slug: 'xc-3000',
        name: 'XC-3000 系列全闪 NAS',
        path: '/product/storage/xc-3000',
        description:
          '信创机架式全闪旗舰，NVMe 企业级 SSD、NVMe-oF 与 RDMA 低时延，适配高性能数据库、金融隔离与媒体等极致性能场景。',
      },
      {
        slug: 'xc-2000',
        name: 'XC-2000 系列高性能 NAS',
        path: '/product/storage/xc-2000',
        description:
          '大型企业全场景存储中枢，双路 CPU、混合架构，亚毫秒级响应与超高 IOPS，支持大规模并发与 PB 级扩展，深度信创适配。',
      },
      {
        slug: 'xc-1000',
        name: 'XC-1000 系列机架式 NAS',
        path: '/product/storage/xc-1000',
        description:
          '面向部门与中型业务，MRSS V3、多规格盘位与扩展柜，RAID6/快照/异地备份兼顾性价比与可靠性。',
      },
      {
        slug: 'xc-08',
        name: 'XC-08 桌面信创 NAS',
        path: '/product/storage/xc-08',
        description:
          '小巧安静高性能，飞腾/海光/兆芯多硬件平台与信创存储 OS，多重隔离、易运维，适合办公共享、热备等场景。',
      },
    ],
  },
];

export function getStorageLeafItems(rows = storageRows) {
  return rows.flatMap((r) => r.items);
}

/** 各型号技术白皮书（部署时将 PDF 放入 public 后把 url 改为如 /storage/whitepapers/xc-3000.pdf） */
export const storageWhitepaperBySlug = {
  'xc-3000': {
    label: '企业级 XC-3000 系列全闪 NAS 存储技术白皮书',
    url: '/storage/whitepapers/xc-3000.docx',
  },
  'xc-2000': {
    label: '企业级 XC-2000 系列 NAS 存储技术白皮书',
    url: '/storage/whitepapers/xc-2000.docx',
  },
  'xc-1000': {
    label: '机架式 XC-1000 系列 NAS 存储技术白皮书',
    url: '/storage/whitepapers/xc-1000.docx',
  },
  'xc-08': {
    label: '桌面 XC-08 信创系列 NAS 存储技术白皮书',
    url: '/storage/whitepapers/xc-08.docx',
  },
};

/** 硬件型号详情页规格（白皮书提炼） */
export const storageSpecMap = {
  '/product/storage/xc-3000': [
    '全闪架构：企业级 NVMe SSD，PCIe 4.0，顺序读写 GB/s 级，随机 IOPS 可达百万级，支持 NVMe-oF。',
    '国产算力：飞腾 S5000C / 海光 5380 等高端 CPU，大容量 ECC 内存，25GbE 起跳并可扩展 100GbE。',
    '数据服务：小文件聚合、大文件并行传输、虚拟化与容器卷、分层/压缩/去重与 QoS。',
    '可靠性：RAID 0/1/5/6/10/50/60、快照/克隆/镜像与 RAID 多重校验，适配核心生产。',
  ],
  '/product/storage/xc-2000': [
    '产品定位：高性能企业级机架 XNAS，「全场景存储中枢」，信创与双路算力兼顾。',
    '性能指标：连续读写高达 2.5GB/s（部分平台更高），随机 IOPS 10 万+，2500+ 并发用户。',
    '扩展能力：扩展柜纵向扩容，容量可达 PB 级，在线扩容不中断业务。',
    '安全运维：本地快照与异地 MRSS Mirror、全链路审计、可视化运维与冗余电源风扇。',
  ],
  '/product/storage/xc-1000': [
    '产品定位：部门与中型业务机架存储，MRSS V3，飞腾/海光/兆芯及兼容平台。',
    '硬件形态：2U/3U/4U 多盘位，最大可扩展至百余块盘，热插拔与模块化设计。',
    '数据保护：RAID6、分钟级快照、本地+异地备份与长期归档能力。',
    '易用性：中文 Web 管理、监控告警与批量自动化运维任务。',
  ],
  '/product/storage/xc-08': [
    '形态与 CPU：8 盘位桌面 NAS，飞腾 D3000 / 海光 3350 / 兆芯等 8 核信创平台。',
    '存储分层：SSD+HDD 智能 Tiering，全闪可达约 1GB/s 级读写（视配置）。',
    '协议与安全：iSCSI/CIFS/NFS/HTTP 等多协议，快照、克隆、镜像与 RAID PZ 等机制。',
    '适用场景：信创办公、档案、监控与中小企业文件共享与归档。',
  ],
};

/** 结构化规格表（当前用于 /product/storage/xc-3000） */
export const storageSpecTableBySlug = {
  'xc-3000': {
    title: 'XC-3000 系列系列产品技术规格',
    columns: ['XC-3024IP', 'XC-3024FP', 'XC-3024HP', 'XC-3024ZP', 'XC-3024LP'],
    columnGroups: [
      {
        key: 'general',
        label: '通用型',
        indexes: [0, 1, 2],
      },
      {
        key: 'nvme',
        label: 'NVMe 全闪极致性能型',
        indexes: [3, 4],
      },
    ],
    rows: [
      {
        label: 'CPU 配置',
        values: ['2 颗 INTEL 6330 20 核', '2 颗飞腾 S5000C 32 核', '2 颗海光 5380 16 核', '2 颗兆芯 40000 16 核', '2 颗鲲鹏 920 32 核'],
      },
      { label: '架构', values: ['2U24 盘位', '2U24 盘位', '2U24 盘位', '2U24 盘位', '2U24 盘位'] },
      { label: '标配内存', values: ['32GB/64GB', '32GB/64GB', '32GB/64GB', '32GB/64GB', '32GB/64GB'] },
      { label: '最大内存', values: ['512GB', '512GB', '512GB', '512GB', '512GB'] },
      {
        label: '硬盘类型',
        values: [
          '2.5 寸 NVMe、SAS、SATA SSD',
          '2.5 寸 NVMe、SAS、SATA SSD',
          '2.5 寸 NVMe、SAS、SATA SSD',
          '2.5 寸 NVMe、SAS、SATA SSD',
          '2.5 寸 NVMe、SAS、SATA SSD',
        ],
      },
      { label: '标配网卡', values: ['2 个千兆', '2 个千兆', '2 个千兆', '2 个千兆', '2 个千兆'] },
      { label: 'PCIe 扩展', values: ['6 个 PCIe', '6 个 PCIe', '6 个 PCIe', '6 个 PCIe', '6 个 PCIe'] },
      {
        label: '最大磁盘',
        values: ['NVMe SSD 24 / SATA SSD 108', 'NVMe SSD 24 / SATA SSD 108', 'NVMe SSD 24 / SATA SSD 108', 'NVMe SSD 24 / SATA SSD 108', 'NVMe SSD 24 / SATA SSD 108'],
      },
      { label: '系统内核', values: ['国产欧拉', '国产欧拉', '国产欧拉', '国产欧拉', '国产欧拉'] },
      { label: '网络协议', values: ['CIFS、NFS、iSCSI、FTP', 'CIFS、NFS、iSCSI、FTP', 'CIFS、NFS、iSCSI、FTP', 'CIFS、NFS、iSCSI、FTP', 'CIFS、NFS、iSCSI、FTP'] },
      {
        label: '标准存储功能',
        values: ['卷管理、共享、快照、压缩、去重、容灾', '卷管理、共享、快照、压缩、去重、容灾', '卷管理、共享、快照、压缩、去重、容灾', '卷管理、共享、快照、压缩、去重、容灾', '卷管理、共享、快照、压缩、去重、容灾'],
      },
      { label: '应用功能', values: ['网盘、备份', '网盘、备份', '网盘、备份', '网盘、备份', '网盘、备份'] },
      { label: '冗余电源', values: ['1300W', '1300W', '1300W', '1300W', '1300W'] },
      {
        label: '尺寸',
        values: [
          '650(L) × 437(W) × 88(H) mm',
          '650(L) × 437(W) × 132(H) mm',
          '650(L) × 437(W) × 176(H) mm',
          '650(L) × 437(W) × 176(H) mm',
          '650(L) × 437(W) × 265(H) mm',
        ],
      },
    ],
  },
  'xc-2000': {
    title: 'XC-2000 系列系列产品技术规格',
    columns: ['XC-2012K/KP', 'XC-2016K/KP', 'XC-2024K/KP', 'XC-2036K/KP', 'XC-2060K/KP'],
    columnGroups: [
      {
        key: 'general',
        label: '通用型',
        indexes: [0, 1, 2],
      },
      {
        key: 'dense',
        label: '高密扩展型',
        indexes: [3, 4],
      },
    ],
    rows: [
      { label: 'CPU 配置', values: ['1/2 颗鲲鹏 920 32 核', '1/2 颗鲲鹏 920 32 核', '1/2 颗鲲鹏 920 32 核', '1/2 颗鲲鹏 920 32 核', '1/2 颗鲲鹏 920 32 核'] },
      { label: '架构', values: ['2U12 盘位', '3U16 盘位', '4U24 盘位', '4U36 盘位', '6U60 盘位'] },
      { label: '标配内存', values: ['64GB/128GB', '64GB/128GB', '64GB/128GB', '64GB/128GB', '64GB/128GB'] },
      { label: '最大内存', values: ['256GB', '256GB', '256GB', '256GB', '256GB'] },
      {
        label: '硬盘类型',
        values: [
          '2.5/3.5 寸 SAS、SATA SSD/HDD',
          '2.5/3.5 寸 SAS、SATA SSD/HDD',
          '2.5/3.5 寸 SAS、SATA SSD/HDD',
          '2.5/3.5 寸 SAS、SATA SSD/HDD',
          '2.5/3.5 寸 SAS、SATA SSD/HDD',
        ],
      },
      { label: '标配网卡', values: ['2 个千兆', '2 个千兆', '2 个千兆', '2 个千兆', '2 个千兆'] },
      { label: 'PCIe 扩展', values: ['4 个 PCIe', '4 个 PCIe', '4 个 PCIe', '4 个 PCIe', '4 个 PCIe'] },
      { label: '最大磁盘', values: ['252', '256', '264', '276', '276'] },
      { label: '系统内核', values: ['国产欧拉', '国产欧拉', '国产欧拉', '国产欧拉', '国产欧拉'] },
      { label: '网络协议', values: ['CIFS、NFS、iSCSI、FTP', 'CIFS、NFS、iSCSI、FTP', 'CIFS、NFS、iSCSI、FTP', 'CIFS、NFS、iSCSI、FTP', 'CIFS、NFS、iSCSI、FTP'] },
      {
        label: '标准存储功能',
        values: ['卷管理、共享、快照、压缩、去重、容灾', '卷管理、共享、快照、压缩、去重、容灾', '卷管理、共享、快照、压缩、去重、容灾', '卷管理、共享、快照、压缩、去重、容灾', '卷管理、共享、快照、压缩、去重、容灾'],
      },
      { label: '应用功能', values: ['网盘、备份', '网盘、备份', '网盘、备份', '网盘、备份', '网盘、备份'] },
      { label: '冗余电源', values: ['800W', '800W', '800W', '1300W', '2000W'] },
      {
        label: '尺寸',
        values: [
          '650(L) × 437(W) × 88(H) mm',
          '650(L) × 437(W) × 132(H) mm',
          '650(L) × 437(W) × 176(H) mm',
          '650(L) × 437(W) × 176(H) mm',
          '650(L) × 437(W) × 265(H) mm',
        ],
      },
    ],
  },
  'xc-1000': {
    title: 'XC-1000 系列系列产品技术规格',
    columns: ['XC-1012Z', 'XC-1016Z', 'XC-1024Z', 'XC-1036Z', 'XC-1060Z'],
    columnGroups: [
      {
        key: 'general',
        label: '通用型',
        indexes: [0, 1, 2],
      },
      {
        key: 'dense',
        label: '高密扩展型',
        indexes: [3, 4],
      },
    ],
    rows: [
      { label: 'CPU 配置', values: ['兆芯 6780', '兆芯 6780', '兆芯 6780', '兆芯 6780', '兆芯 6780'] },
      { label: '架构', values: ['2U12 盘位', '3U16 盘位', '4U24 盘位', '4U36 盘位', '6U60 盘位'] },
      { label: '标配内存', values: ['16GB', '16GB', '16GB', '16GB', '16GB'] },
      { label: '最大内存', values: ['64GB', '64GB', '64GB', '64GB', '64GB'] },
      {
        label: '硬盘类型',
        values: [
          '2.5/3.5 寸 SAS、SATA SSD/HDD',
          '2.5/3.5 寸 SAS、SATA SSD/HDD',
          '2.5/3.5 寸 SAS、SATA SSD/HDD',
          '2.5/3.5 寸 SAS、SATA SSD/HDD',
          '2.5/3.5 寸 SAS、SATA SSD/HDD',
        ],
      },
      { label: '标配网卡', values: ['2 个千兆', '2 个千兆', '2 个千兆', '2 个千兆', '2 个千兆'] },
      { label: 'PCIe 扩展', values: ['1 个 PCIe', '1 个 PCIe', '1 个 PCIe', '1 个 PCIe', '1 个 PCIe'] },
      { label: '最大磁盘', values: ['252', '256', '264', '276', '276'] },
      { label: '系统内核', values: ['国产欧拉', '国产欧拉', '国产欧拉', '国产欧拉', '国产欧拉'] },
      { label: '网络协议', values: ['CIFS、NFS、iSCSI、FTP', 'CIFS、NFS、iSCSI、FTP', 'CIFS、NFS、iSCSI、FTP', 'CIFS、NFS、iSCSI、FTP', 'CIFS、NFS、iSCSI、FTP'] },
      {
        label: '标准存储功能',
        values: ['卷管理、共享、快照、压缩、去重、容灾', '卷管理、共享、快照、压缩、去重、容灾', '卷管理、共享、快照、压缩、去重、容灾', '卷管理、共享、快照、压缩、去重、容灾', '卷管理、共享、快照、压缩、去重、容灾'],
      },
      { label: '应用功能', values: ['网盘、备份', '网盘、备份', '网盘、备份', '网盘、备份', '网盘、备份'] },
      { label: '冗余电源', values: ['550W', '550W', '800W', '800W', '2000W'] },
      {
        label: '尺寸',
        values: [
          '650(L) × 437(W) × 88(H) mm',
          '650(L) × 437(W) × 132(H) mm',
          '650(L) × 437(W) × 176(H) mm',
          '650(L) × 437(W) × 176(H) mm',
          '650(L) × 437(W) × 265(H) mm',
        ],
      },
    ],
  },
  'xc-08': {
    title: 'XC-08 系列系列产品技术规格',
    columns: ['XC-08F', 'XC-08Z', 'XC-08H'],
    columnGroups: [
      {
        key: 'desktop',
        label: '桌面信创系列',
        indexes: [0, 1, 2],
      },
    ],
    rows: [
      { label: 'CPU 配置', values: ['1 颗飞腾 D3000', '1 颗兆芯 6780', '1 颗海光 3350'] },
      { label: '架构', values: ['桌面 8 盘位', '桌面 8 盘位', '桌面 8 盘位'] },
      { label: '标配内存', values: ['16GB', '16GB', '16GB'] },
      { label: '最大内存', values: ['64GB', '64GB', '64GB'] },
      { label: '硬盘类型', values: ['2.5/3.5 寸 SAS、SATA SSD/HDD', '2.5/3.5 寸 SAS、SATA SSD/HDD', '2.5/3.5 寸 SAS、SATA SSD/HDD'] },
      { label: '标配网卡', values: ['2 个千兆', '2 个千兆', '2 个千兆'] },
      { label: 'PCIe 扩展', values: ['4 个 PCIe', '4 个 PCIe', '4 个 PCIe'] },
      { label: '最大磁盘', values: ['8', '8', '8'] },
      { label: '系统内核', values: ['国产欧拉', '国产欧拉', '国产欧拉'] },
      { label: '网络协议', values: ['CIFS、NFS、iSCSI、FTP', 'CIFS、NFS、iSCSI、FTP', 'CIFS、NFS、iSCSI、FTP'] },
      { label: '标准存储功能', values: ['卷管理、共享、快照、压缩、去重、容灾', '卷管理、共享、快照、压缩、去重、容灾', '卷管理、共享、快照、压缩、去重、容灾'] },
      { label: '应用功能', values: ['网盘、备份', '网盘、备份', '网盘、备份'] },
      { label: '冗余电源', values: ['800W', '800W', '800W'] },
      {
        label: '尺寸',
        values: [
          '305(W) × 310(D) × 221(H) mm',
          '305(W) × 310(D) × 221(H) mm',
          '305(W) × 310(D) × 221(H) mm',
        ],
      },
    ],
  },
};

function normalizeWhitepaperText(raw) {
  return String(raw || '')
    .replace(/\r/g, '')
    .replace(/\u00A0/g, ' ')
    // 源文本为 OCR/提取结果，常以多个空格分隔段落，这里规整为换行
    .replace(/[ \t]{2,}/g, '\n')
    .replace(/\n{2,}/g, '\n')
    .trim();
}

/** 四个硬件型号的白皮书全文（由原始 DOCX 提取文本规整而来） */
export const storageWhitepaperFullTextBySlug = {
  'xc-3000': normalizeWhitepaperText(xc3000WhitepaperRaw),
  'xc-2000': normalizeWhitepaperText(xc2000WhitepaperRaw),
  'xc-1000': normalizeWhitepaperText(xc1000WhitepaperRaw),
  'xc-08': normalizeWhitepaperText(xc08WhitepaperRaw),
};

function extractSpecSection(text) {
  const full = String(text || '');
  const marker = /(?:系列|XNAS\s*XC-08系列)\s*系列产品技术规格/g;
  const matches = [...full.matchAll(marker)];
  if (!matches.length) return full;
  // 目录里通常也会出现同名章节，取最后一次命中（正文末尾章节）更准确
  const last = matches[matches.length - 1];
  if (last.index == null) return full;
  return full.slice(last.index).trim();
}

/** 四个硬件型号页仅展示“系列产品技术规格”章节 */
export const storageSpecSectionTextBySlug = {
  'xc-3000': extractSpecSection(storageWhitepaperFullTextBySlug['xc-3000']),
  'xc-2000': extractSpecSection(storageWhitepaperFullTextBySlug['xc-2000']),
  'xc-1000': extractSpecSection(storageWhitepaperFullTextBySlug['xc-1000']),
  'xc-08': extractSpecSection(storageWhitepaperFullTextBySlug['xc-08']),
};
